import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { DialogType } from "app/types";
import { AppDispatch } from "app/store";
import { uploadImageAsset } from "app/api";
import { actions as configActions } from "app/configSlice";
import { getAccountUsers } from "features/account/api";

export const useFetchUserData = () => {
  const dispatch = useDispatch<AppDispatch>();

  const handleFetchParentsUser = useCallback(
    async ({ uid, callback }: { uid: string; callback?: (redirectTo: string) => void }) => {
      try {
        await dispatch(getAccountUsers({ uid })).unwrap();
        if (callback) {
          callback("/parent");
        }
      } catch (error) {
        console.error(error);
        if (callback) {
          callback("/auth/login");
        }
      }
    },
    [dispatch],
  );

  const handleFetchUserData = useCallback(
    async (_params: { parent_uid: string; uid: string }) => {
      // Stub - not used in parents app directly
    },
    [],
  );

  return { handleFetchParentsUser, handleFetchUserData };
};

export const useDialog = () => {
  const dispatch = useDispatch<AppDispatch>();
  return useCallback(
    ({
      type,
      value,
      args,
    }: {
      type: DialogType;
      value: boolean;
      args?: { [key: string]: string };
    }) => {
      dispatch(
        configActions.updateDialog({
          type,
          value,
          args,
        }),
      );
    },
    [dispatch],
  );
};

// Image asset picker for child player icons
export const usePickImageAsset = () => {
  const handleDialog = useDialog();
  return async (
    file: File,
    uid: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    callback?: (asset: any) => void,
    fallback?: () => void,
  ) => {
    const uuid = uuidv4();
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = function () {
      handleDialog({ type: DialogType.LOADING, value: true });
      uploadImageAsset({
        uid,
        fileBlob: file,
        object_key: `users/${uid}/assets/drawing/${uuid}.png`,
        filename: file.name,
        tags: "image",
        metadata: {
          filename: encodeURIComponent(file.name),
          size: String(file.size),
          width: String(image.width),
          height: String(image.height),
        },
        contentType: file.type,
      })
        .then((response) => {
          if (callback) {
            callback(response.data);
          }
        })
        .catch((error) => {
          console.error(error);
          if (fallback) {
            fallback();
          }
        })
        .finally(() => handleDialog({ type: DialogType.LOADING, value: false }));
      URL.revokeObjectURL(objectUrl);
    };
    image.src = objectUrl;
  };
};

// Image asset picker for parent icons
export const usePickParentImageAsset = () => {
  const handleDialog = useDialog();
  return async (
    file: File,
    uid: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    callback?: (asset: any) => void,
    fallback?: () => void,
  ) => {
    const uuid = uuidv4();
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = function () {
      handleDialog({ type: DialogType.LOADING, value: true });
      uploadImageAsset({
        uid,
        fileBlob: file,
        object_key: `users/${uid}/assets/drawing/${uuid}.png`,
        filename: file.name,
        tags: "image",
        metadata: {
          filename: encodeURIComponent(file.name),
          size: String(file.size),
          width: String(image.width),
          height: String(image.height),
        },
        contentType: file.type,
      })
        .then((response) => {
          if (callback) {
            callback(response.data);
          }
        })
        .catch((error) => {
          console.error(error);
          if (fallback) {
            fallback();
          }
        })
        .finally(() => handleDialog({ type: DialogType.LOADING, value: false }));
      URL.revokeObjectURL(objectUrl);
    };
    image.src = objectUrl;
  };
};
